import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

const STORAGE_KEY = "photomoto_cart_v1";
const CartContext = createContext(null);

function readInitial() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(readInitial);

  // Persist to localStorage whenever cart changes
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
      // ignore quota errors
    }
  }, [items]);

  // Cross-tab sync
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY && e.newValue !== null) {
        try {
          setItems(JSON.parse(e.newValue) || []);
        } catch { /* ignore */ }
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const addItem = useCallback((image) => {
    if (!image || !image.id) return;
    setItems((prev) => {
      if (prev.some((it) => it.id === image.id)) {
        toast.info("Already in your basket");
        return prev;
      }
      const item = {
        id: image.id,
        title: image.title,
        price: image.price,
        currency: (image.currency || "gbp").toLowerCase(),
      };
      toast.success(`Added · ${image.title}`);
      return [...prev, item];
    });
  }, []);

  const removeItem = useCallback((id) => {
    setItems((prev) => prev.filter((it) => it.id !== id));
  }, []);

  const clearCart = useCallback(() => setItems([]), []);

  const addMany = useCallback((images) => {
    setItems((prev) => {
      const existing = new Set(prev.map((it) => it.id));
      const fresh = (images || [])
        .filter((img) => img && img.id && !existing.has(img.id))
        .map((img) => ({
          id: img.id,
          title: img.title,
          price: img.price,
          currency: (img.currency || "gbp").toLowerCase(),
        }));
      if (fresh.length === 0) {
        toast.info("Everything is already in your basket");
        return prev;
      }
      toast.success(`Added ${fresh.length} photo${fresh.length > 1 ? "s" : ""} to your basket`);
      return [...prev, ...fresh];
    });
  }, []);

  const hasItem = useCallback((id) => items.some((it) => it.id === id), [items]);

  const value = useMemo(() => {
    const count = items.length;
    const currencies = new Set(items.map((it) => it.currency));
    const currency = currencies.size === 1 ? [...currencies][0] : null;
    const total = items.reduce((sum, it) => sum + Number(it.price || 0), 0);
    return { items, count, total, currency, mixedCurrency: currencies.size > 1, addItem, addMany, removeItem, clearCart, hasItem };
  }, [items, addItem, addMany, removeItem, clearCart, hasItem]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}
